// How an employee row PRINTS — the pieces every employee surface needs before
// it can draw a person (name, initials, status pill). They live here rather
// than in one screen because the detail PAGE (SEM-6354) and the list read the
// same row; a second copy of the lifecycle→tone map would drift the moment a
// new lifecycle state lands.

import { type EmployeeLifecycleState } from "@novemai/hris-sdk";
import { type BadgeTone } from "@novemai/components";

// Lifecycle → badge tone (novem pill language; personnel.jsx status pills).
// Covers the CANONICAL machine (packages/hris-sdk lifecycle.ts): pre_hire →
// onboarding → active → on_leave/suspended → terminated → rehired/alumnus.
// Typed as an exhaustive Record over EmployeeLifecycleState (not a loose
// string key) so tsc rejects a build the moment a new lifecycle state is
// added to the SDK without an entry here — the SAME guard as LIFECYCLE_KEY
// below.
export const LIFECYCLE_TONE: Readonly<Record<EmployeeLifecycleState, BadgeTone>> = {
  pre_hire: "info",
  onboarding: "info",
  active: "success",
  on_leave: "warning",
  suspended: "warning",
  terminated: "neutral",
  rehired: "success",
  alumnus: "neutral",
};

// Lifecycle → i18n key (people.lifecycle.* — en-US.json / tr-TR.json). The
// `satisfies Record<EmployeeLifecycleState, string>` target forces tsc to
// check the map is exhaustive over every EmployeeLifecycleState member: add a
// 9th state to packages/hris-sdk/src/entities.ts without adding its entry
// here, and the build fails at THIS map (rather than silently rendering a
// raw `people.lifecycle.<state>` fallback string at runtime, which is what
// the old `t(\`people.lifecycle.${state}\`)` dynamic template literal did —
// the i18n:extract static-literal scanner can't see through a template
// literal, so a missing translation for a new state was invisible until
// someone spotted the raw key rendered in the UI). Declared `as const` so
// each value stays its literal string type (not widened to `string`) —
// harmless here, but keeps the map usable as a literal-key source elsewhere.
export const LIFECYCLE_KEY = {
  pre_hire: "people.lifecycle.pre_hire",
  onboarding: "people.lifecycle.onboarding",
  active: "people.lifecycle.active",
  on_leave: "people.lifecycle.on_leave",
  suspended: "people.lifecycle.suspended",
  terminated: "people.lifecycle.terminated",
  rehired: "people.lifecycle.rehired",
  alumnus: "people.lifecycle.alumnus",
} as const satisfies Record<EmployeeLifecycleState, string>;

// Structural, not `EmployeeRow`: a personnel row and a directory entry
// (directory-api.ts) both print a person, and only these two fields decide how.
interface NamedPerson {
  readonly legal_first_name: string;
  readonly legal_last_name: string;
}

export function fullName(row: NamedPerson): string {
  return `${row.legal_first_name} ${row.legal_last_name}`;
}

export function initialsOf(row: NamedPerson): string {
  return `${row.legal_first_name.slice(0, 1)}${row.legal_last_name.slice(0, 1)}`;
}
